// frontend/packages/business-console/src/components/media/AltTextRequiredDialog.jsx
//
// Prompts for alt text before inserting media into markdown body.
// Pre-fills a sensible default from the filename so the user can
// accept with one click instead of being blocked.

import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
} from "@mui/material";

function generateDefaultAlt(assetTitle) {
  if (!assetTitle) return "Image";

  return (
    String(assetTitle)
      // Strip file extension
      .replace(/\.[^.]+$/, "")
      // Strip cld: prefix and path segments (keep last segment)
      .replace(/^cld:.*\//, "")
      // Replace hyphens, underscores, camelCase with spaces
      .replace(/[-_]/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      // Collapse whitespace
      .replace(/\s+/g, " ")
      .trim()
      // Capitalize first letter
      .replace(/^./, (c) => c.toUpperCase()) || "Image"
  );
}

export default function AltTextRequiredDialog({
  open,
  onClose,
  onSubmit,
  assetTitle,
}) {
  const [altText, setAltText] = useState("");

  useEffect(() => {
    if (open) {
      setAltText(generateDefaultAlt(assetTitle));
    }
  }, [open, assetTitle]);

  const handleUseDefault = () => {
    const text = altText.trim() || generateDefaultAlt(assetTitle);
    onSubmit?.(text);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Describe this image</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          A short description helps search engines and screen readers
          understand this image. You can edit it or accept the suggestion.
        </Typography>

        <TextField
          sx={{ mt: 2 }}
          label="Alt text"
          value={altText}
          onChange={(e) => setAltText(e.target.value)}
          fullWidth
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter" && altText.trim()) {
              e.preventDefault();
              onSubmit?.(altText.trim());
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!altText.trim()}
          onClick={() => onSubmit?.(altText.trim())}
          sx={{ fontWeight: 900 }}
        >
          Use this description
        </Button>
      </DialogActions>
    </Dialog>
  );
}
